'use client';

import { AuthWarningToast, useAuthWarningToast } from '@/components/auth/AuthWarningToast';
import { AuthSelectBox } from '@/components/auth/AuthSelectBox';
import { LocationInfoGuidelineModal } from '@/components/auth/policy/LocationInfoGuidelineModal';
import { PrivacyPolicyModal } from '@/components/auth/policy/PrivacyPolicyModal';
import { TermsOfServiceModal } from '@/components/auth/policy/TermOfServiceModal';
import { cn } from '@/shared/cn';
import { appendSafeonSearchParams } from '@/shared/forced-mobile/forced-mobile';
import { useIsSafeonFlutterClient } from '@/shared/forced-mobile/ForcedMobileProvider';
import { AUTH_LANDING_PAGE_URL } from '@/shared/routes';
import styles from '@/styles/screens/login-page.module.css';
import { Eye, EyeOff, X } from 'lucide-react';
import Link from 'next/link';
import { signIn } from 'next-auth/react';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useMemo, useRef, useState } from 'react';
import { z } from 'zod';

declare global {
  interface Window {
    safeonMobileDevice?: { device_uid: string; platform: 'android' | 'ios'; os_version: string; app_name: string; package_name: string; app_version: string; build_number: string };
  }
}

const LOGIN_ERROR_MESSAGES: Record<string, string> = {
  account_unavailable: '계정 상태가 정상 사용 가능 상태가 아닙니다. 관리자에게 문의해 주세요.',
  field_access_denied: '요청한 현장에 접근 권한이 없습니다.',
  invalid_credentials: '이메일/아이디 또는 비밀번호가 올바르지 않습니다.',
  server_unavailable: '로그인 서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.',
  signin_failed: '로그인 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
  temporarily_locked: '로그인 시도 횟수를 초과했습니다. 잠시 후 다시 시도해 주세요.',
  worker_field_erp_required: '로그인할 현장을 다시 확인해 주세요.',
  worker_field_selection_required: '로그인할 현장을 선택해 주세요.',
  field_selection_required: '로그인할 현장을 다시 선택해 주세요.',
  ambiguous_account: '선택한 현장의 계정을 구분할 수 없습니다. 관리자에게 문의해 주세요.',
};

const loginFieldsSchema = z.object({
  fields: z.array(z.object({ uid: z.string().uuid(), erp_id: z.string().trim().min(1), works_title: z.string().nullable().optional() })),
});
type LoginFieldOption = z.infer<typeof loginFieldsSchema>['fields'][number];

/**
 * 로그인 후 이동 주소를 같은 origin으로 제한하고 기존 쿼리를 보존합니다.
 */
function resolveLoginCallbackUrl(url: string) {
  const origin = window.location.origin;
  let targetUrl = new URL(AUTH_LANDING_PAGE_URL, origin);
  try {
    const parsedUrl = new URL(url, origin);
    // callbackUrl은 외부 입력이므로 외부 주소, 실행 스킴, URL 내 인증정보를 허용하지 않습니다.
    if (parsedUrl.origin === origin && parsedUrl.protocol === targetUrl.protocol && !parsedUrl.username && !parsedUrl.password) {
      targetUrl = parsedUrl;
    }
  } catch {
    // 해석할 수 없는 이동 주소는 기본 대시보드로 복구합니다.
  }

  return targetUrl.toString();
}

export function LoginForm() {
  const isFlutterClient = useIsSafeonFlutterClient();
  const searchParams = useSearchParams();
  const callbackUrl = useMemo(() => appendSafeonSearchParams(searchParams.get('callbackUrl') ?? AUTH_LANDING_PAGE_URL, searchParams), [searchParams]);

  const [userid, setUserid] = useState('');
  const [password, setPassword] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [erpId, setErpId] = useState('');
  const [loginFields, setLoginFields] = useState<LoginFieldOption[]>([]);
  const [remember, setRemember] = useState(true);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const loginInFlight = useRef(false);
  const credentialsRevision = useRef(0);
  const { warning, showWarning, clearWarning } = useAuthWarningToast();

  const fieldLabelClass = 'grid min-w-0 gap-1.5';
  const fieldTitleClass = 'font-[Pretendard] text-[13px] leading-none font-medium tracking-[-0.02em] text-[#221E1F]';
  const inputClass = `min-w-0 h-[56px] w-full rounded-[3px] border-[1px] border-[#e7e7e7] bg-white px-4 text-base text-slate-900 outline-none transition placeholder:font-[Pretendard] placeholder:text-[15px] placeholder:leading-none placeholder:font-normal placeholder:tracking-[-0.02em] placeholder:text-[#9E9E9E] focus:border-slate-500  sm:text-[15px] ${styles.loginInput}`;
  const policyButtonClass =
    'cursor-pointer border-none bg-transparent p-0 font-[Pretendard] text-[13px] leading-none font-normal tracking-[-0.02em] text-[#9E9E9E] transition hover:text-slate-500';

  /**
   * NextAuth가 반환한 에러/코드를 사용자용 메시지로 변환합니다.
   */
  const resolveLoginErrorMessage = (errorCode?: string | null) => {
    if (!errorCode) {
      return LOGIN_ERROR_MESSAGES.signin_failed;
    }

    return LOGIN_ERROR_MESSAGES[errorCode] ?? LOGIN_ERROR_MESSAGES.signin_failed;
  };

  const resetLoginFields = () => {
    credentialsRevision.current += 1;
    setLoginFields([]);
    if (loginFields.length > 0) setErpId('');
    clearWarning();
  };

  /**
   * 최종 Credentials 로그인을 실행합니다.
   */
  const submitCredentialsLogin = async (normalizedErpId: string): Promise<'signed_in' | 'select_field' | 'failed'> => {
    const nextCallbackUrl = resolveLoginCallbackUrl(callbackUrl);
    if (isFlutterClient && !window.safeonMobileDevice) {
      showWarning('앱 기기 정보를 확인할 수 없습니다. 앱을 다시 실행해주세요.');
      return 'failed';
    }
    const mobileDevice = window.safeonMobileDevice;
    const result = await signIn('credentials', {
      redirect: false,
      userid: userid.trim(),
      password,
      callbackUrl: nextCallbackUrl,
      rememberLogin: remember ? 'true' : 'false',
      ...(normalizedErpId ? { erpId: normalizedErpId } : {}),
      ...(isFlutterClient && mobileDevice
        ? { mobile_device: JSON.stringify({ device_uid: mobileDevice.device_uid, platform: mobileDevice.platform, os_version: mobileDevice.os_version }) }
        : {}),
    });

    if (!result?.ok || result.error) {
      if (!normalizedErpId && result?.code === 'worker_field_selection_required') return 'select_field';
      showWarning(resolveLoginErrorMessage(result?.code ?? result?.error));
      return 'failed';
    }

    // 인증 쿠키가 바뀐 직후에는 로그인 전 Router Cache와 전역 상태를 재사용하지 않고 서버에서 화면을 다시 구성합니다.
    window.location.replace(nextCallbackUrl);
    return 'signed_in';
  };

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();
    // 상태가 화면에 반영되기 전의 연속 제출도 막아 인증 요청을 한 번만 실행합니다.
    if (loginInFlight.current) {
      return;
    }
    clearWarning();

    if (!userid.trim() || !password) {
      showWarning('아이디 또는 비밀번호를 입력하세요');
      return;
    }

    if (isFlutterClient && !window.safeonMobileDevice) {
      showWarning('앱 기기 정보를 확인할 수 없습니다. 앱을 다시 실행해주세요.');
      return;
    }

    loginInFlight.current = true;
    setLoading(true);
    const revision = credentialsRevision.current;
    let signedIn = false;

    try {
      let selectedErpId = erpId.trim();
      if (loginFields.length === 0) {
        const result = await submitCredentialsLogin(selectedErpId);
        if (result !== 'select_field') {
          signedIn = result === 'signed_in';
          return;
        }
        const response = await fetch('/api/auth/login-fields', {
          method: 'POST',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userid: userid.trim(), password }),
          signal: AbortSignal.timeout(15000),
        });
        const payload: unknown = await response.json();
        if (revision !== credentialsRevision.current) return;
        if (!response.ok) {
          showWarning(
            response.status === 401
              ? LOGIN_ERROR_MESSAGES.invalid_credentials
              : response.status === 400
                ? '현장 선택 로그인을 처리할 수 없습니다. 관리자에게 문의해 주세요.'
                : response.status === 403
                  ? '현장이 등록되지 않았거나 접근할 수 없는 계정입니다. 관리자에게 문의해 주세요.'
                  : response.status === 429
                    ? LOGIN_ERROR_MESSAGES.temporarily_locked
                    : LOGIN_ERROR_MESSAGES.server_unavailable
          );
          return;
        }
        const parsed = loginFieldsSchema.safeParse(payload);
        if (!parsed.success || parsed.data.fields.length === 0) {
          showWarning('로그인 가능한 현장을 확인할 수 없습니다. 관리자에게 문의해 주세요.');
          return;
        }
        if (parsed.data.fields.length > 1) {
          setLoginFields(parsed.data.fields);
          return;
        }
        selectedErpId = parsed.data.fields[0].erp_id;
      } else if (!loginFields.some(field => field.erp_id === selectedErpId)) {
        showWarning('로그인할 현장을 선택해 주세요.');
        return;
      }
      signedIn = (await submitCredentialsLogin(selectedErpId)) === 'signed_in';
      if (!signedIn) {
        setLoginFields([]);
        setErpId('');
      }
    } catch {
      // 인증 통신 오류의 상세 내용은 노출하지 않고 입력값을 유지해 재시도할 수 있게 합니다.
      showWarning(resolveLoginErrorMessage('signin_failed'));
    } finally {
      if (!signedIn) {
        loginInFlight.current = false;
        setLoading(false);
      }
    }
  };

  return (
    <section className="flex w-full min-w-0 items-center justify-center" aria-label="로그인 폼 섹션">
      <AuthWarningToast warning={warning} onDismiss={clearWarning} />
      {loading ? (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-white/72 backdrop-blur-[2px]" role="status" aria-live="polite" aria-label="로그인 처리 중">
          <div className="grid min-w-45 justify-items-center gap-3 rounded-lg border border-slate-200 bg-white px-6 py-5 shadow-[0_18px_45px_rgba(15,23,42,0.16)]">
            <span className="h-8 w-8 animate-spin rounded-full border-3 border-slate-200 border-t-[var(--color-app-brand-strong)]" aria-hidden="true" />
            <span className="text-sm font-semibold text-slate-700">로그인 중...</span>
          </div>
        </div>
      ) : null}
      <div className="mx-auto w-full max-w-[26.25rem] min-w-0 px-4 py-6 sm:px-5">
        <div className="bg-white">
          <div>
            <div className="mb-10 text-center">
              <Image src="https://storage.hanlimiot.kr/sus/frontend/public/images/logo/sacon.png" className="mx-auto" alt="SACON" width={130} height={50} priority />
              <p className="mt-4 text-center font-[Pretendard] text-[15px] leading-none font-normal tracking-[-0.02em] text-slate-600">
                {loginFields.length > 1 ? '로그인할 현장을 선택해 주세요.' : '서비스를 이용하기 위해 로그인해 주시기 바랍니다.'}
              </p>
            </div>

            <form onSubmit={onSubmit} noValidate className="grid w-full min-w-0 gap-[20px]">
              {loginFields.length === 0 ? (
                <>
                  <label className={fieldLabelClass}>
                    <span className={fieldTitleClass}>이메일 또는 아이디</span>
                    <span className="relative block">
                      <input
                        value={userid}
                        onChange={e => {
                          setUserid(e.target.value);
                          resetLoginFields();
                        }}
                        disabled={loading}
                        required
                        placeholder="이메일 또는 아이디를 입력해 주세요"
                        autoComplete="username"
                        className={cn(inputClass, userid ? 'pr-12' : '')}
                      />
                      {userid ? (
                        <button
                          type="button"
                          onClick={() => {
                            setUserid('');
                            resetLoginFields();
                          }}
                          disabled={loading}
                          aria-label="이메일 또는 아이디 입력 내용 지우기"
                          className="absolute top-1/2 right-3 flex size-8 -translate-y-1/2 cursor-pointer items-center justify-center border-none bg-transparent p-0 text-[#9E9E9E] transition">
                          <X className="size-5" strokeWidth={1.8} aria-hidden="true" />
                        </button>
                      ) : null}
                    </span>
                  </label>

                  <label className={fieldLabelClass}>
                    <span className={fieldTitleClass}>비밀번호</span>
                    <span className="relative block">
                      <input
                        type={isPasswordVisible ? 'text' : 'password'}
                        value={password}
                        onChange={e => {
                          setPassword(e.target.value);
                          resetLoginFields();
                        }}
                        disabled={loading}
                        required
                        placeholder="비밀번호를 입력해 주세요"
                        autoComplete="current-password"
                        className={cn(inputClass, password ? 'pr-20' : 'pr-12')}
                      />
                      <span className="absolute top-1/2 right-2 flex -translate-y-1/2 items-center">
                        <button
                          type="button"
                          onClick={() => setIsPasswordVisible(visible => !visible)}
                          aria-label={isPasswordVisible ? '비밀번호 숨기기' : '비밀번호 보기'}
                          aria-pressed={isPasswordVisible}
                          className="flex size-8 cursor-pointer items-center justify-center border-none bg-transparent p-0 text-[#9E9E9E] transition">
                          {isPasswordVisible ? <Eye className="size-5" strokeWidth={1.8} aria-hidden="true" /> : <EyeOff className="size-5" strokeWidth={1.8} aria-hidden="true" />}
                        </button>
                        {password ? (
                          <button
                            type="button"
                            onClick={() => {
                              setPassword('');
                              resetLoginFields();
                            }}
                            disabled={loading}
                            aria-label="비밀번호 입력 내용 지우기"
                            className="flex size-8 cursor-pointer items-center justify-center border-none bg-transparent p-0 text-[#9E9E9E] transition">
                            <X className="size-5" strokeWidth={1.8} aria-hidden="true" />
                          </button>
                        ) : null}
                      </span>
                    </span>
                  </label>
                  <label className={fieldLabelClass}>
                    <span className={fieldTitleClass}>현장 ERP ID</span>
                    <input
                      value={erpId}
                      onChange={event => {
                        resetLoginFields();
                        setErpId(event.target.value);
                      }}
                      disabled={loading}
                      placeholder="현장 ERP ID 입력 (생략 가능)"
                      className={inputClass}
                    />
                  </label>
                </>
              ) : (
                <fieldset disabled={loading} className="m-0 min-w-0 border-0 p-0">
                  <div className={fieldLabelClass}>
                    <span className={fieldTitleClass}>로그인할 현장</span>
                    <AuthSelectBox
                      aria-label="로그인 현장 선택"
                      value={erpId}
                      onValueChange={setErpId}
                      options={loginFields.map(field => ({ value: field.erp_id, label: field.works_title ? `${field.works_title} (${field.erp_id})` : field.erp_id }))}
                      placeholder="현장명 또는 ERP ID를 선택해 주세요"
                      className={inputClass}
                    />
                  </div>
                </fieldset>
              )}

              {loginFields.length === 0 ? (
                <button
                  type="button"
                  onClick={() => setRemember(prev => !prev)}
                  aria-pressed={remember}
                  className={cn(
                    'inline-flex h-5 cursor-pointer items-center gap-1.5 justify-self-start border-none bg-transparent p-0 font-[Pretendard] text-[13px] leading-none font-normal tracking-[-0.02em] transition',
                    remember ? 'text-[#221E1F]' : 'text-[#9E9E9E]'
                  )}>
                  <span className="flex size-4 shrink-0 items-center justify-center">
                    <Image
                      className="block size-full"
                      src={
                        remember
                          ? 'https://storage.hanlimiot.kr/sus/frontend/public/images/icons/check.svg'
                          : 'https://storage.hanlimiot.kr/sus/frontend/public/images/icons/nocheck.svg'
                      }
                      alt={remember ? '로그인 상태 저장 활성화' : '로그인 상태 저장 비활성화'}
                      width={16}
                      height={16}
                    />
                  </span>
                  <span className="mb-[1px] flex h-4 items-center">로그인 상태 저장</span>
                </button>
              ) : null}

              <button
                type="submit"
                disabled={loading}
                className={cn(
                  'mt-5 h-14 w-full rounded border-none bg-[var(--color-app-brand-strong)] font-[Pretendard] text-[16px] leading-none font-semibold tracking-[-0.02em] text-white transition',
                  loading ? 'cursor-default opacity-70' : 'cursor-pointer hover:opacity-92'
                )}>
                {loading ? '확인 중...' : '로그인'}
              </button>

              {loginFields.length > 1 ? (
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => {
                    setPassword('');
                    setIsPasswordVisible(false);
                    resetLoginFields();
                  }}
                  className="flex h-14 items-center justify-center rounded border border-[var(--color-app-border)] text-base font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-70">
                  계정 다시 입력
                </button>
              ) : (
                <Link
                  href="/signup"
                  className="flex h-14 items-center justify-center rounded border border-[var(--color-app-border)] text-base font-semibold text-slate-700 transition hover:bg-slate-50 lg:hidden">
                  회원가입
                </Link>
              )}
            </form>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap justify-center gap-2.5 text-xs text-slate-400">
          <button type="button" onClick={() => setIsTermsOpen(true)} className={policyButtonClass}>
            이용약관
          </button>
          <span className="text-[#e7e7e7]" aria-hidden="true">
            |
          </span>
          <button type="button" onClick={() => setIsPrivacyOpen(true)} className={policyButtonClass}>
            개인정보처리방침
          </button>
          <span className="text-[#e7e7e7]" aria-hidden="true">
            |
          </span>
          <button type="button" onClick={() => setIsLocationOpen(true)} className={policyButtonClass}>
            위치기반서비스 처리방침
          </button>
        </div>

        <TermsOfServiceModal isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} />
        <PrivacyPolicyModal isOpen={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} />
        <LocationInfoGuidelineModal isOpen={isLocationOpen} onClose={() => setIsLocationOpen(false)} />
      </div>
    </section>
  );
}
